import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import Minus from '../../../images/amexgdab2b/minus-circle.svg';
import Plus from '../../../images/amexgdab2b/plus-circle.svg';
import { handleMktAnalytics } from '../../AmexMarketingUtils/AmexUtils';
import { CTA_EVENT_NAME } from '../../AmexMarketingUtils/constants';
import './AmexGDAB2BWhyWeLove.scss';

const MAX_SECTIONS = 3;

const AmexGDAB2BWhyWeLove = (
  {
    baseRequestUrl,
    contentData: {
      sections: sectionsAll,
      title,
      section_image_height: sectionImgHeight,
      expanded_section_copy_height: expandCopyHeight,
    },
    matchMedia,
    Analytics,
  }
) => {
  const sectionsWithDuplicatesIds = sectionsAll?.filter((item) => !(!item.id || !item.heading
    || !item.description || !item.image));

  const sections = sectionsWithDuplicatesIds?.filter((obj, index) => index === sectionsWithDuplicatesIds.findIndex((o) => obj.id === o.id)).slice(0, MAX_SECTIONS);
  const sectionsLen = sections?.length || 0;

  const hideWhyWeLove = sectionsLen === 0 || !sectionImgHeight || !expandCopyHeight;
  const isMobile = !matchMedia.lg;

  if (hideWhyWeLove) return false;

  const [activeSectionId, setActiveSectionId] = useState(sections[0].id);

  const findSectionToExpand = (sectionId) => {
    let idOfExpandedSection = 0;

    if (sectionId !== activeSectionId) {
      idOfExpandedSection = sectionId;
    } else {
      sections.forEach((item, index) => {
        if (item.id === sectionId) {
          if ((sectionsLen - 1 !== index)) {
            idOfExpandedSection = sections[index + 1].id;
          } else if ((sectionsLen - 1 === index)) {
            idOfExpandedSection = 1;
          }
        }
      });
    }

    return idOfExpandedSection;
  };

  const renderSectionsContent = () => sections
    .map((section) => {
      const sanitizedSectionDescription = sanitizeString(section.description);

      return (
        <li key={section.description}>
          {(section.heading && sectionsLen > 1) && (
            <h3
              className={`AmexGDAB2BWhyWeLove__titleWrapper ${(activeSectionId === section.id) ? 'active' : ''}`}
            >
              <Button
                variant="no-style"
                type="button"
                id={`AmexGDAB2BWhyWeLoveTitle${section.id}`}
                aria-controls={`AmexGDAB2BWhyWeLoveContent${section.id}`}
                className={`AmexGDAB2BWhyWeLove__button ${(activeSectionId === section.id) ? 'active' : ''}`}
                aria-expanded={(activeSectionId === section.id)}
                onClick={() => {
                  const extraParam = {
                    action_name: 'Why We Love - Click',
                    item_name: section.heading,
                  };
                  handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParam);
                  setActiveSectionId(findSectionToExpand(section.id));
                }}
              >
                <span className="AmexGDAB2BWhyWeLove__title">
                  {(activeSectionId === section.id) && (
                  <span aria-hidden="true">
                    <Minus className="AmexGDAB2BWhyWeLove__icon" />
                  </span>
                  )}

                  {(activeSectionId !== section.id) && (
                  <span>
                    <Plus className="AmexGDAB2BWhyWeLove__icon" />
                  </span>
                  )}
                  {section.heading}
                </span>
              </Button>
            </h3>
          )}

          {(section.heading && sectionsLen === 1) && (
            <h3 className="AmexGDAB2BWhyWeLove__title single">
              {section.heading}
            </h3>
          )}

          <div
            role="region"
            id={`AmexGDAB2BWhyWeLoveContent${section.id}`}
            aria-labelledby={`AmexGDAB2BWhyWeLoveTitle${section.id}`}
            className={`AmexGDAB2BWhyWeLove__contentWrapper ${(activeSectionId === section.id) ? 'is-open' : ''} ${(sectionsLen === 1) ? 'single' : ''}`}
          >
            <div className="AmexGDAB2BWhyWeLove__inner">
              <div
                className="AmexGDAB2BWhyWeLove__text"
                {...(!isMobile ? {
                  style: { minHeight: expandCopyHeight },
                } : {})}
                dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                  __html: sanitizedSectionDescription,
                }}
              />
              {isMobile && (
                <div className="AmexGDAB2BWhyWeLove__image">
                  <img src={`${baseRequestUrl}AmexGDAB2B/images/WhyWeLoveIt/${section.image}`} alt="" />
                </div>
              )}
            </div>
          </div>
        </li>
      );
    });

  const renderDesktop = () => {
    const imageShowed = sections.find((item) => item.id === activeSectionId).image;
    return (
      <>
        <div
          className="AmexGDAB2BWhyWeLove__image"
          style={{
            minHeight: sectionImgHeight,
            backgroundImage: `url(${baseRequestUrl}AmexGDAB2B/images/WhyWeLoveIt/${imageShowed})`,
          }}
        />
        <div className="AmexGDAB2BWhyWeLove__content">
          <ol className="AmexGDAB2BWhyWeLove__items">
            {sections && renderSectionsContent()}
          </ol>
        </div>
      </>
    );
  };

  const renderMobile = () => (
    <div className="AmexGDAB2BWhyWeLove__content">
      <ol className="AmexGDAB2BWhyWeLove__items">
        {sections && renderSectionsContent()}
      </ol>
    </div>
  );

  return (
    <section className="AmexGDAB2BWhyWeLove">
      <div className="container">
        <h2 className="AmexGDAB2BWhyWeLove__mainTitle">
          {title}
        </h2>
        <div className="AmexGDAB2BWhyWeLove__wrapper">
          {!isMobile && renderDesktop()}
          {isMobile && renderMobile()}
        </div>
      </div>
    </section>
  );
};

AmexGDAB2BWhyWeLove.propTypes = {
  baseRequestUrl: PropTypes.string.isRequired,
  contentData: PropTypes.shape({
    event_tag: PropTypes.string,
    hide_section: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    section_image_height: PropTypes.string.isRequired,
    expanded_section_copy_height: PropTypes.string.isRequired,
    sections: PropTypes.arrayOf(PropTypes.shape(
      {
        id: PropTypes.number,
        title: PropTypes.string,
        description: PropTypes.string,
        image: PropTypes.string,
      }
    )).isRequired,
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
};

export default AmexGDAB2BWhyWeLove;
